import { tss } from 'tss-react/mui';

export const ROW_HEIGHT = 56;
export const GROUP_HEADER_HEIGHT = 28;
export const POPOVER_WIDTH = 360;
export const POPOVER_MAX_HEIGHT = 420;
export const VIRTUALIZATION_THRESHOLD = 50;

export const useStyles = tss.create(({ theme }) => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    minWidth: 0,
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    lineHeight: 1.4,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover:not(:disabled)': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  triggerOpen: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
  },
  triggerError: {
    borderColor: theme.palette.error.main,
    '&:hover:not(:disabled)': {
      borderColor: theme.palette.error.main,
    },
    '&:focus-visible': {
      borderColor: theme.palette.error.main,
      boxShadow: `0 0 0 2px ${theme.palette.error.light}`,
    },
  },
  triggerLabel: {
    flex: 1,
    minWidth: 0,
    textAlign: 'start',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  triggerPlaceholder: {
    color: theme.palette.extendedGrey?.neutral ?? theme.palette.text.secondary,
  },
  triggerChevron: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    transition: 'transform 0.15s ease',
  },
  triggerChevronOpen: {
    transform: 'rotate(180deg)',
  },
  popoverPaper: {
    width: POPOVER_WIDTH,
    maxWidth: '92vw',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0px 8px 24px rgba(42, 53, 71, 0.16)',
    border: `1px solid ${theme.palette.divider}`,
  },
  searchHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchIcon: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 14,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    minWidth: 0,
    '&::placeholder': {
      color: theme.palette.text.secondary,
    },
  },
  searchClear: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    '&:hover': {
      background: theme.palette.extendedGrey?.lighter,
      color: theme.palette.text.primary,
    },
  },
  list: {
    maxHeight: POPOVER_MAX_HEIGHT,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '4px 0',
    outline: 'none',
  },
  virtualSpacer: {
    width: '100%',
    flexShrink: 0,
  },
  groupHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    height: GROUP_HEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    background: theme.palette.extendedGrey?.lighter ?? theme.palette.grey[100],
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: ROW_HEIGHT,
    padding: '0 16px',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'start',
    fontFamily: 'inherit',
    color: theme.palette.text.primary,
    transition: 'background-color 0.1s ease',
    '&:hover': {
      background: theme.palette.action.hover,
    },
    '&:focus-visible': {
      outline: 'none',
      background: theme.palette.action.hover,
    },
  },
  rowActive: {
    background: theme.palette.action.hover,
  },
  rowSelected: {
    background: theme.palette.action.hover,
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rowName: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowPrompt: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowMeta: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  typeChip: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: '2px 6px',
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    background: theme.palette.background.default,
    whiteSpace: 'nowrap',
  },
  typeChipSystem: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.light,
  },
  typeChipCustom: {
    color: theme.palette.warning.main,
    borderColor: theme.palette.warning.main,
    background: theme.palette.warning.light,
  },
  typeChipNoRole: {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    background: theme.palette.extendedGrey?.lighter ?? theme.palette.grey[100],
  },
  checkIcon: {
    color: theme.palette.primary.main,
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    marginInlineStart: 4,
  },
  emptyState: {
    padding: '24px 16px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'center',
  },
  emptyStateAction: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: 13,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  pinnedSeparator: {
    height: 1,
    background: theme.palette.divider,
    margin: '4px 0',
  },
}));
